import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  ArrowsLeftRight, Plus, Minus, ArrowsClockwise, GitBranch, CheckCircle,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function DiffSummaryChips({ summary }) {
  if (!summary) return null;
  const Chip = ({ count, label, cls, Icon }) => (
    count > 0 ? (
      <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded border ${cls}`}>
        <Icon size={11} weight="bold" />
        {count} {label}
      </span>
    ) : null
  );
  const allZero = summary.added === 0 && summary.removed === 0 && summary.changed === 0;
  return (
    <div className="flex flex-wrap gap-1.5 items-center">
      <Chip count={summary.added} label="added" cls="bg-[#DEF7EC] border-[#31C48D] text-[#03543F]" Icon={Plus} />
      <Chip count={summary.removed} label="removed" cls="bg-[#FDE8E8] border-[#F8B4B4] text-[#9B1C1C]" Icon={Minus} />
      <Chip count={summary.changed} label="changed" cls="bg-[#FEF3C7] border-[#F3CC64] text-[#723B13]" Icon={ArrowsClockwise} />
      <span className="text-[10px] text-[#4B5563] tabular-nums">· {summary.unchanged} unchanged</span>
      {allZero && <span className="text-[10px] text-[#03543F] font-semibold">No ingredient changes</span>}
    </div>
  );
}

function DiffDetail({ diff }) {
  if (!diff) return null;
  const { added = [], removed = [], changed = [] } = diff;
  if (added.length === 0 && removed.length === 0 && changed.length === 0) return null;
  return (
    <div className="space-y-2 mt-3">
      {added.length > 0 && (
        <div className="border border-[#31C48D] bg-[#F3FBF7] rounded p-2.5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#03543F] mb-1.5 flex items-center gap-1">
            <Plus size={11} weight="bold" /> Added ({added.length})
          </p>
          {added.map(a => (
            <div key={a.inci_name} className="text-xs flex items-center gap-2">
              <span className="font-semibold text-[#111827]">{a.inci_name}</span>
              <span className="text-[#4B5563] font-mono tabular-nums">{a.concentration}%</span>
              {a.function && <span className="text-[10px] text-[#4B5563]">· {a.function}</span>}
            </div>
          ))}
        </div>
      )}
      {removed.length > 0 && (
        <div className="border border-[#F8B4B4] bg-[#FDF7F7] rounded p-2.5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#9B1C1C] mb-1.5 flex items-center gap-1">
            <Minus size={11} weight="bold" /> Removed ({removed.length})
          </p>
          {removed.map(a => (
            <div key={a.inci_name} className="text-xs flex items-center gap-2 line-through opacity-70">
              <span className="font-semibold text-[#111827]">{a.inci_name}</span>
              <span className="text-[#4B5563] font-mono tabular-nums">{a.concentration}%</span>
            </div>
          ))}
        </div>
      )}
      {changed.length > 0 && (
        <div className="border border-[#F3CC64] bg-[#FEFBEC] rounded p-2.5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#723B13] mb-1.5 flex items-center gap-1">
            <ArrowsClockwise size={11} weight="bold" /> Changed ({changed.length})
          </p>
          {changed.map(c => (
            <div key={c.inci_name} className="text-xs">
              <span className="font-semibold text-[#111827]">{c.inci_name}</span>
              {Object.entries(c.changes).map(([field, { before, after }]) => (
                <span key={field} className="ml-2 text-[#4B5563]">
                  {field}: <span className="line-through">{String(before ?? '—')}</span> → <span className="font-semibold text-[#111827]">{String(after ?? '—')}</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Version Timeline + Diff section — rendered on AssessmentPage when a
 * formulation has a parent or descendants. Calls /api/formulations/{id}/version-tree. */
export default function VersionDiffSection({ formulation }) {
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!formulation?.id) return;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/formulations/${formulation.id}/version-tree`, { withCredentials: true });
        setTree(r.data);
      } catch (e) {
        console.warn('[VersionDiffSection] tree fetch failed:', e?.response?.status || e?.message);
      } finally { setLoading(false); }
    })();
  }, [formulation?.id]);

  if (loading) return null;
  if (!tree?.chain || tree.chain.length <= 1) return null;  // only show if 2+ versions exist

  const currentNode = tree.chain.find(n => n.is_current);

  return (
    <div className="card-flat mb-8" data-testid="version-diff-section">
      <div className="p-4 border-b border-gray-200 flex items-center gap-2">
        <GitBranch size={20} className="text-[#111827]" weight="duotone" />
        <h2 className="text-sm font-semibold text-[#111827]">Version Timeline</h2>
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] bg-[#F3F4F6] px-2 py-0.5 rounded ml-2">
          {tree.chain.length} version{tree.chain.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Compact timeline */}
      <div className="p-4 overflow-x-auto">
        <div className="flex items-center gap-3 min-w-max">
          {tree.chain.map((n, idx) => (
            <React.Fragment key={n.id}>
              <a href={`/assessment/${n.id}`}
                 className={`flex flex-col items-start gap-1 px-3 py-2 rounded border transition-colors ${
                   n.is_current
                     ? 'border-[#FF4E6A] bg-[#FFF5F7]'
                     : 'border-gray-200 hover:bg-gray-50'
                 }`}
                 data-testid={`version-node-${n.version}`}>
                <span className="flex items-center gap-1.5">
                  <span className="text-xs font-mono font-bold text-[#111827]">v{n.version}</span>
                  {n.is_current && <CheckCircle size={11} weight="fill" className="text-[#FF4E6A]" />}
                </span>
                <span className="text-[10px] text-[#4B5563]">
                  {n.created_at ? new Date(n.created_at).toLocaleDateString() : '—'}
                </span>
                {n.diff_vs_previous && <DiffSummaryChips summary={n.diff_vs_previous.summary} />}
              </a>
              {idx < tree.chain.length - 1 && <ArrowsLeftRight size={16} className="text-gray-300 flex-shrink-0" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Current version's diff (vs previous) */}
      {currentNode?.diff_vs_previous && (
        <div className="p-4 border-t border-gray-100" data-testid="current-version-diff">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold uppercase tracking-wider text-[#4B5563]">
              Changes in v{currentNode.version} vs previous
            </p>
            {currentNode.version_note && (
              <p className="text-xs text-[#4B5563] italic max-w-md truncate" title={currentNode.version_note}>
                "{currentNode.version_note}"
              </p>
            )}
          </div>
          <DiffSummaryChips summary={currentNode.diff_vs_previous.summary} />
          <DiffDetail diff={currentNode.diff_vs_previous} />
        </div>
      )}
    </div>
  );
}
